import { useCallback, useEffect, useRef, useState } from 'react';
import { useFullscreenViewer } from '../../contexts/FullscreenViewerContext';

interface PanState {
  x: number;
  y: number;
}

export function FullscreenViewer() {
  const { isOpen, mediaItems, currentIndex, closeViewer, goNext, goPrev } =
    useFullscreenViewer();

  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState<PanState>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const currentItem = mediaItems[currentIndex] ?? null;
  const isSingleItem = mediaItems.length <= 1;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === mediaItems.length - 1;
  const isVideo = currentItem?.mediaType === 'video';

  // Reset zoom/pan on image change or close
  useEffect(() => {
    setZoomed(false);
    setPan({ x: 0, y: 0 });
  }, [currentIndex, isOpen]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Keyboard handler in capture phase
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (zoomed) {
          setZoomed(false);
          setPan({ x: 0, y: 0 });
        } else {
          closeViewer();
        }
        return;
      }

      if (e.key === 'ArrowLeft' && !zoomed) {
        e.preventDefault();
        e.stopPropagation();
        goPrev();
        return;
      }

      if (e.key === 'ArrowRight' && !zoomed) {
        e.preventDefault();
        e.stopPropagation();
        goNext();
        return;
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, zoomed, closeViewer, goNext, goPrev]);

  const clampPan = useCallback(
    (px: number, py: number): PanState => {
      const img = imgRef.current;
      const container = containerRef.current;
      if (!img || !container) return { x: px, y: py };

      const maxX = Math.max(0, (img.naturalWidth - container.clientWidth) / 2);
      const maxY = Math.max(0, (img.naturalHeight - container.clientHeight) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, px)),
        y: Math.min(maxY, Math.max(-maxY, py)),
      };
    },
    [],
  );

  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Don't toggle zoom if we were dragging
      if (dragging) return;
      if (zoomed) {
        setZoomed(false);
        setPan({ x: 0, y: 0 });
      } else {
        setZoomed(true);
      }
    },
    [zoomed, dragging],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      if (!zoomed) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(false);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
    },
    [zoomed, pan],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      const start = dragStartRef.current;
      if (!start) return;
      setDragging(true);
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      setPan(clampPan(start.panX + dx, start.panY + dy));
    },
    [clampPan],
  );

  const handlePointerUp = useCallback(() => {
    // Allow click handler to see dragging state this frame,
    // then reset on next microtask
    const wasDragging = dragStartRef.current !== null && dragging;
    dragStartRef.current = null;
    if (wasDragging) {
      // Reset dragging after the click event fires (same event dispatch)
      requestAnimationFrame(() => setDragging(false));
    }
  }, [dragging]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        closeViewer();
      }
    },
    [closeViewer],
  );

  const stopProp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (!isOpen || !currentItem) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        className="btn btn-circle btn-ghost text-white hover:bg-white/20 absolute top-4 right-4 z-10"
        onClick={(e) => {
          e.stopPropagation();
          closeViewer();
        }}
        aria-label="Close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Left arrow */}
      {!isSingleItem && (
        <button
          className="btn btn-circle btn-ghost text-white absolute left-4 z-10"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          disabled={isFirst}
          aria-label="Previous"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Right arrow */}
      {!isSingleItem && (
        <button
          className="btn btn-circle btn-ghost text-white absolute right-4 z-10"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          disabled={isLast}
          aria-label="Next"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Media content */}
      {isVideo ? (
        <video
          className="max-w-[90vw] max-h-[90vh]"
          controls
          autoPlay
          onClick={stopProp}
          src={currentItem.url}
        />
      ) : (
        <img
          ref={imgRef}
          src={currentItem.url}
          draggable={false}
          className={
            zoomed
              ? 'select-none cursor-grab active:cursor-grabbing'
              : 'select-none object-contain max-w-[90vw] max-h-[90vh] cursor-zoom-in'
          }
          style={
            zoomed
              ? { transform: `translate(${pan.x}px, ${pan.y}px)` }
              : undefined
          }
          onClick={handleImageClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          alt=""
        />
      )}

      {/* Counter */}
      {!isSingleItem && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-3 py-1 rounded-full">
          {currentIndex + 1} / {mediaItems.length}
        </div>
      )}
    </div>
  );
}
